
import { useEffect, useCallback, useRef } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { toast } from '@/utils/toast';

/**
 * Enhanced hook to manage microphone and camera activation with improved
 * immediate enabling/disabling specifically for chat sessions
 */
export function useDeviceSafetyCheck(isChatActive: boolean = false, isChatEnded: boolean = false) {
  const { 
    toggleMicrophone, 
    toggleVideo,
    isMicrophoneEnabled,
    isVideoEnabled
  } = useChat();
  
  // Reference to store the last state to avoid redundant operations
  const lastStateRef = useRef({
    chatActive: false,
    chatEnded: false
  });
  
  // Handle immediate device activation when chat becomes active
  useEffect(() => {
    // Only process if there's an actual state change
    if (lastStateRef.current.chatActive !== isChatActive || 
        lastStateRef.current.chatEnded !== isChatEnded) {
      
      // Update the ref
      lastStateRef.current = {
        chatActive: isChatActive,
        chatEnded: isChatEnded
      };
      
      // When chat becomes active, immediately enable devices
      if (isChatActive && !isChatEnded) {
        console.log("Safety: Chat active - enabling devices immediately");
        
        // Enable the mic and camera - critical section for immediate activation
        toggleMicrophone(true);
        toggleVideo(true);
        
        // Make sure devices are enabled - double check with shorter timeout
        setTimeout(() => {
          if (!isMicrophoneEnabled) {
            console.log("Safety: Microphone not enabled after initial toggle, retrying...");
            toggleMicrophone(true);
          }
          
          if (!isVideoEnabled) {
            console.log("Safety: Camera not enabled after initial toggle, retrying...");
            toggleVideo(true);
          }
        }, 50);
      }
      
      // When chat ends or becomes inactive, immediately disable devices
      if (isChatEnded || !isChatActive) {
        console.log("Safety: Chat ended/inactive - disabling devices immediately");
        toggleMicrophone(false);
        toggleVideo(false);
        
        // Double check that devices are actually disabled with shorter timeout
        setTimeout(() => {
          if (isMicrophoneEnabled) {
            console.log("Safety: Microphone still enabled after chat ended, turning off again...");
            toggleMicrophone(false);
          }
          
          if (isVideoEnabled) {
            console.log("Safety: Camera still enabled after chat ended, turning off again...");
            toggleVideo(false);
          }
        }, 50);
      }
    }
  }, [isChatActive, isChatEnded, toggleMicrophone, toggleVideo, isMicrophoneEnabled, isVideoEnabled]);
  
  // Safety check before page unload or visibility changes
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log("Safety: Page unloading - disabling devices");
      toggleMicrophone(false);
      toggleVideo(false);
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        console.log("Safety: Page hidden - disabling devices");
        toggleMicrophone(false);
        toggleVideo(false);
      } else if (document.visibilityState === 'visible' && isChatActive && !isChatEnded) {
        console.log("Safety: Page visible and chat active - re-enabling devices");
        toggleMicrophone(true);
        toggleVideo(true);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Final cleanup - ensure devices are off when hook unmounts
      console.log("Safety: Hook unmounting - disabling devices");
      toggleMicrophone(false);
      toggleVideo(false);
    };
  }, [isChatActive, isChatEnded, toggleMicrophone, toggleVideo]);
  
  // Force disable function for emergency use
  const forceDisableDevices = useCallback(() => {
    console.log("Safety: Force disabling all devices");
    toggleMicrophone(false);
    toggleVideo(false);
    
    // Double check with shorter timeout for faster response
    setTimeout(() => {
      if (isMicrophoneEnabled || isVideoEnabled) {
        console.log("Safety: Devices still on after force disable. Retrying...");
        toggleMicrophone(false);
        toggleVideo(false);
      }
    }, 50);
    
    return true;
  }, [toggleMicrophone, toggleVideo, isMicrophoneEnabled, isVideoEnabled]);
  
  // Enhanced performance optimization
  const enhancePerformance = useCallback(() => {
    if (isChatActive && !isChatEnded) {
      // Optimize for active chat - make sure devices are enabled
      if (!isMicrophoneEnabled) {
        console.log("Safety: Performance check - enabling mic");
        toggleMicrophone(true);
      }
      
      if (!isVideoEnabled) {
        console.log("Safety: Performance check - enabling camera");
        toggleVideo(true);
      }
    }
  }, [isChatActive, isChatEnded, toggleMicrophone, toggleVideo, isMicrophoneEnabled, isVideoEnabled]);

  return {
    forceDisableDevices,
    enhancePerformance
  };
}
